import { useEffect, useRef, useState } from "react";
import { NavLink } from "react-router-dom";
import { LayoutGrid, Wallet as WalletIcon, MessageCircle, User } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

// WeChat-style super-app bottom bar: Hub · Services · [Wallet] · Chats · Me
const LEFT = [
  { to: "/", label: "Hub", icon: LayoutGrid, end: true },
  { to: "/services", label: "Services", icon: LayoutGrid },
];

const Tab = ({ to, label, icon: Icon, end, badge }) => (
  <NavLink to={to} end={end} data-testid={`bottomnav-${label.toLowerCase()}`}
    className={({ isActive }) =>
      `relative flex flex-col items-center justify-center gap-0.5 py-2 text-[11px] font-body transition-colors ${isActive ? "text-pula-green" : "text-pula-inksoft"}`}>
    <span className="relative">
      <Icon className="w-5 h-5" />
      {badge > 0 && (
        <span key={badge} data-testid="bottomnav-chats-badge"
          className="animate-badge-pop absolute -top-1.5 -right-2 min-w-[16px] h-4 px-1 rounded-full bg-pula-accent text-white text-[9px] font-bold grid place-items-center border border-white">
          {badge > 99 ? "99+" : badge}
        </span>
      )}
    </span>
    {label}
  </NavLink>
);

export function MobileBottomNav() {
  const { user } = useAuth();
  const [unread, setUnread] = useState(0);
  const alive = useRef(true);

  useEffect(() => {
    if (!user) return undefined;
    alive.current = true;
    const load = () => Promise.all([
      api.get("/messenger/unread-total").then(({ data }) => data.count || 0).catch(() => 0),
      api.get("/messages/unread-count").then(({ data }) => data.count || 0).catch(() => 0),
    ]).then(([a, b]) => { if (alive.current) setUnread(a + b); });
    load();
    const t = setInterval(load, 15000);
    return () => { alive.current = false; clearInterval(t); };
  }, [user]);

  if (!user) return null;

  return (
    <nav data-testid="mobile-bottom-nav"
      className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-pula-bg/95 backdrop-blur border-t border-[var(--pula-border)] pb-[env(safe-area-inset-bottom)]">
      <div className="relative grid grid-cols-5 items-end">
        <Tab {...LEFT[0]} />
        <Tab {...LEFT[1]} />
        {/* Center: emphasised Wallet (the WeChat "power" button) */}
        <div className="flex justify-center">
          <NavLink to="/wallet" data-testid="bottomnav-wallet"
            className={({ isActive }) =>
              `-mt-6 flex flex-col items-center gap-0.5 ${isActive ? "text-pula-green" : "text-pula-ink"}`}>
            <span className="w-14 h-14 rounded-full bg-pula-green text-white grid place-items-center shadow-lg ring-4 ring-pula-bg">
              <WalletIcon className="w-6 h-6" />
            </span>
            <span className="text-[11px] font-body -mt-0.5">Wallet</span>
          </NavLink>
        </div>
        <Tab to="/chat" label="Chats" icon={MessageCircle} badge={unread} />
        <Tab to="/me" label="Me" icon={User} />
      </div>
    </nav>
  );
}
